/**
 * PersonsPage - Cadastro único de Pessoas (fornecedor, fabricante, prestador...)
 *
 * Um CPF/CNPJ = uma pessoa, com papéis acumuláveis (BR-PES-001/002).
 * Totalmente separado do cadastro de Clientes (BR-PES-003).
 *
 * RELEASE: 2026-10-12 v1.2.0 - Ação "Acesso ao portal" para prestadores (Portal do Prestador).
 * RELEASE: 2026-10-14 v1.2.0 - Clique na linha abre PersonDetailsModal (Dados; Prestador com O.S e Movimentação).
 * RELEASE: 2026-10-06 v1.1.0 - Submenus especialistas (/tenant/persons/:role): lista já filtrada
 *          pelo tipo, permissão própria por tipo e "Nova pessoa" com o tipo marcado.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 1 do módulo Estoque).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Contact, KeyRound, MoreHorizontal, Pencil, Plus, Power } from 'lucide-react';
import { ProviderPortalAccessDialog } from '@/components/persons/ProviderPortalAccessDialog';
import {
  ListPageLayout,
  ListPageHeader,
  ListPageFiltersBar,
  ListPageContent,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { PERSON_ROLES, usePersonsList, usePersonsMutations, type Person, type PersonsFilters, PERSON_ROLE_SECTIONS } from '@/hooks/usePersons';
import { PersonFormDialog, formatPersonDocument } from '@/components/persons/PersonFormDialog';
import { PersonDetailsModal } from '@/components/persons/PersonDetailsModal';

/** Rótulo amigável do papel. */
const roleLabel = (role: string) => PERSON_ROLES.find((r) => r.value === role)?.label ?? role;

export default function PersonsPage() {
  const { canCreate, canEdit, isAdmin } = usePermissionsContext();
  // Submenu especialista: o tipo vem da rota (ex.: /tenant/persons/fornecedores)
  const { role: roleSlug } = useParams<{ role?: string }>();
  const section = roleSlug ? PERSON_ROLE_SECTIONS.find((s) => s.slug === roleSlug) ?? null : null;
  const fixedRole = section?.role ?? null;
  const permKey = section?.menuKey ?? 'persons';
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<PersonsFilters>({ search: '', role: fixedRole ?? 'all', status: 'active', page: 1, pageSize: 25 });

  // Troca de submenu: reaplica o tipo fixo
  useEffect(() => {
    setFilters((f) => ({ ...f, role: fixedRole ?? 'all', page: 1 }));
  }, [fixedRole]);
  const [editor, setEditor] = useState<{ open: boolean; person: Person | null }>({ open: false, person: null });
  // Portal do Prestador: acesso do prestador escolhido
  const [portalPerson, setPortalPerson] = useState<Person | null>(null);
  // v1.2.0: clique na linha abre os detalhes (edição pelo botão Editar do modal ou pelo menu "...")
  const [details, setDetails] = useState<Person | null>(null);
  const { data, isLoading } = usePersonsList(filters);
  const { upsertPerson } = usePersonsMutations();

  // Busca com atraso curto para não disparar uma consulta por tecla
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const allowCreate = isAdmin || canCreate(permKey);
  const allowEdit = isAdmin || canEdit(permKey);
  const items = data?.items ?? [];

  /** Ativa/inativa reenviando o cadastro completo (mesma RPC de edição). */
  const toggleActive = (p: Person) => {
    upsertPerson.mutate({
      id: p.id,
      data: {
        person_type: p.person_type, document: p.document, name: p.name, legal_name: p.legal_name,
        trade_name: p.trade_name, email: p.email, phone: p.phone, cep: p.cep, street: p.street,
        address_number: p.address_number, complement: p.complement, district: p.district, city: p.city,
        state: p.state, notes: p.notes, linked_customer_id: p.linked_customer_id, is_active: !p.is_active,
      },
    });
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title={section?.title ?? 'Pessoas'}
        description={section ? `Pessoas com o tipo ${section.label}. Cada CPF/CNPJ tem um único cadastro.` : 'Fornecedores, fabricantes, prestadores e demais parceiros. Cada CPF/CNPJ tem um único cadastro.'}
        actions={allowCreate ? (
          <Button size="xs" onClick={() => setEditor({ open: true, person: null })}>
            <Plus className="mr-1.5 h-4 w-4" /> {section?.newLabel ?? 'Nova pessoa'}
          </Button>
        ) : undefined}
      />

      <ListPageFiltersBar>
        <Input
          className={`${LIST_FILTERS_INPUT_CLASS} min-w-0 flex-1 sm:max-w-sm`}
          value={searchInput}
          maxLength={100}
          placeholder="Buscar por nome ou CPF/CNPJ..."
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {!fixedRole && <Select value={filters.role} onValueChange={(v) => setFilters((f) => ({ ...f, role: v as PersonsFilters['role'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os papéis</SelectItem>
            {PERSON_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>}
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v as PersonsFilters['status'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full rounded-lg" />
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Contact className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nenhuma pessoa encontrada.</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="w-44">CPF/CNPJ</TableHead>
                  <TableHead>Papéis</TableHead>
                  <TableHead className="w-40">Cidade</TableHead>
                  <TableHead className="w-24">Situação</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => setDetails(p)}>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                      {p.trade_name && p.trade_name !== p.name && <div className="text-xs text-muted-foreground">{p.trade_name}</div>}
                    </TableCell>
                    <TableCell className="tabular-nums text-sm">{formatPersonDocument(p.document) || '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {p.roles.length === 0 ? <span className="text-xs text-muted-foreground">Sem papel</span> : p.roles.map((r) => (
                          <Badge key={r} variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">{roleLabel(r)}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{p.city ? `${p.city}${p.state ? `/${p.state}` : ''}` : '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider">
                        <StatusDot tone={p.is_active ? 'emerald' : 'muted'} />
                        {p.is_active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {allowEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditor({ open: true, person: p })}>
                              <Pencil className="mr-2 h-4 w-4" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleActive(p)}>
                              <Power className="mr-2 h-4 w-4" /> {p.is_active ? 'Inativar' : 'Ativar'}
                            </DropdownMenuItem>
                            {p.roles.includes('service_provider') && (
                              <DropdownMenuItem onClick={() => setPortalPerson(p)}>
                                <KeyRound className="mr-2 h-4 w-4" /> Acesso ao portal
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <PaginationFooter
          totalItems={data?.total ?? 0}
          currentPage={filters.page}
          pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </ListPageContent>

      <ProviderPortalAccessDialog open={!!portalPerson} onOpenChange={(o) => !o && setPortalPerson(null)}
        person={portalPerson ? { id: portalPerson.id, name: portalPerson.name, email: portalPerson.email } : null} />
      <PersonDetailsModal person={details} open={!!details} onOpenChange={(o) => !o && setDetails(null)}
        onEdit={allowEdit ? (p) => setEditor({ open: true, person: p }) : undefined} />
      <PersonFormDialog
        open={editor.open}
        person={editor.person}
        defaultRole={fixedRole}
        onOpenChange={(open) => setEditor((c) => ({ ...c, open }))}
        onOpenExisting={(person) => setEditor({ open: true, person })}
      />
    </ListPageLayout>
  );
}
